<template>
  <div
    :class="['advanced-select', 'position-relative', 'text-start', { active: isDropdownVisible }]"
    :data-value="latestChangedValue"
  >
    <div :class="['input-group', size ? ('input-group-' + size) : '']">
      <div
          ref="formControl"
          :id="id"
          tabindex="0"
          :title="mainTooltip"
          :class="[
            'form-control',
            'position-relative advanced-select-wrapper position-relative',
            controlClass,
            size ? ('form-control-' + size) : '',
            { 'disabled': disabled },
            { 'border-danger': hasError }
          ]"
          @click="showDropdown()"
          @focus="showDropdown()"
          @keydown="handleKeyDown"
      >
        <div v-if="multiple && taggable" class="d-flex flex-wrap pe-3">
          <!-- Show first N tags -->
          <Tag
              v-for="(selectedItem, index) in Object.values(selectedItemsMap).slice(0, tagDisplayLimit)"
              :key="'option-tag-' + getItemValue(selectedItem)"
              :title="getItemText(selectedItem)"
              :description="getItemText(selectedItem, 'description')"
              :showTooltip="showTagTooltip"
              :noRemove="disabled || !canRemoveItem(selectedItem)"
              :tagBgColor="getTagBgColor(selectedItem)"
              @remove="handleItemToggle(selectedItem)"
          />

          <!-- +X more -->
          <Tag
              v-if="Object.keys(selectedItemsMap).length > tagDisplayLimit"
              :title="'+' + (Object.keys(selectedItemsMap).length - tagDisplayLimit) + ' more'"
              :description="Object.values(selectedItemsMap).slice(tagDisplayLimit).map(i => getItemText(i, 'description')).join(', ')"
              :showTooltip="true"
              :noRemove="true"
              :useTitleForTooltip="true"
              tagBgColor="bg-secondary text-white"
          />
        </div>

        <input
            :value="inputValue"
            @input="onInput"
            ref="searchInput"
            autocomplete="off"
            :class="inputClass"
            :readonly="!searchable"
            :disabled="disabled"
            :placeholder="inputPlaceholder"
            :style="isInputVisible ? '' : 'max-height: 0px'"
            @keyup.enter="addMultiOptionsText()"
            @focus.stop.prevent="showDropdown()"
        />

        <span
          v-if="this.limit && this.limit > 1 && (this.selectedItemsMap && (Object.keys(this.selectedItemsMap).length >= this.limit))"
          v-tooltip="{
            text: $t('advanced-select.limit_warning'),
            theme: {
              color: '#fff',
              placement: 'left',
              backgroundColor: '#000000'
            }
          }"
          class="warning-icon"
        >
          <i class="bi bi-exclamation-circle"/>
        </span>

        <template v-else>
          <InputClearIcon
            v-if="Object.keys(this.selectedItemsMap).length > 0 && !disabled && !hideClearIcon"
            @mousedown.native.stop.prevent
            @click.native.stop.prevent="reset"
          />
          <span v-else class="bi bi-chevron-down floating-icon"/>
        </template>
      </div>
      <span
          v-if="['=', '!='].includes(currentComparisonMode)"
          class="input-group-text cursor-pointer"
          @click.stop="toggleComparisonMode"
      >
        {{ currentComparisonMode === '=' ? '=' : '≠' }}
      </span>
    </div>

    <div
        ref="dropdownWrapper"
        v-if="isDropdownVisible"
        :class="['d-inline-block', 'dropdown-wrapper', 'border', 'rounded-bottom', 'shadow', dropdownWrapperClass, limitedWrapperSize ? 'limited-wrapper-size' : '']"
        :style="dropdownWrapperStyle"
    >
      <ul ref="dropDownsList">
        <slot name="option-selector">
          <template v-if="multiple && !limit">
            <li class="option bg-light px-2" @mousedown.stop.prevent="selectAllFiltered">
              <i class="bi bi-check-lg"></i> {{ $t('advanced-select.select_all') }}
            </li>
            <li class="option bg-light border-bottom px-2" @mousedown.stop.prevent="deselectAllFiltered">
              <i class="bi bi-x-lg"></i> {{ $t('advanced-select.deselect_all') }}
            </li>
          </template>
        </slot>

        <template v-if="addNewOption">
          <li
              ref="option"
              :key="'add-new-option-' + addNewOption.value"
              :data-option-value="addNewOption.value"
              :class="[
                'option',
                { 'bg-info': activeOptionIndex === filteredItems.length },
                getItemClass(addNewOption)
              ]"
              @mousedown.stop.prevent="handleItemToggle(addNewOption)"
          >
            <slot name="option" :item="addNewOption">
              <div
                v-tooltip="{
                  text: $t('advanced-select.new_option'),
                  theme: {
                    color: '#fff',
                    placement: 'top',
                    backgroundColor: '#000000',
                  }
                }"
                class="px-2"
              >
                + {{ addNewOption.text }}
              </div>
            </slot>
          </li>
        </template>

        <span v-else-if="isLoadingOption">
          <spinner variant="success" />
        </span>

        <li v-else-if="!filteredItems.length">
          <div class="px-2 py-2">
            {{ $t(emptyListText) }}
          </div>
        </li>

        <template v-else>
          <li
              :ref="el => optionRefs[index] = el"
              v-for="(item, index) in filteredItems"
              :key="'option-' + getItemValue(item)"
              :class="[
                'option',
                item.disabled ? 'option-disabled' : '',
                { 'bg-info': activeOptionIndex === index, 'selected': isItemSelected(item) && activeOptionIndex !== index },
                getItemClass(item)
              ]"
              @mousedown.stop.prevent="handleItemToggle(item)"
          >
            <slot name="option" :item="item">
              <div
                  v-if="showOptionTooltip && item.description"
                  v-tooltip="{
                    text: item.description,
                    theme: {
                      color: '#fff',
                      placement: 'top',
                      backgroundColor: '#000000',
                    }
                  }"
                  class="px-2"
              >
                {{ getItemText(item) }}
              </div>
              <div v-else class="px-2">
                {{ getItemText(item) }}
              </div>
            </slot>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script src="./AdvancedSelect.js" />
<style src="./AdvancedSelect.scss" lang="scss" />

